import { useEffect, useRef, useState } from "react";
import { animate, useInView } from "framer-motion";
import { Reveal } from "./Reveal";

const STATS = [
  { value: 1500, suffix: "+", label: "Participants & families served by TNS since 2015" },
  { value: 3, prefix: "$", suffix: "M+", label: "Raised for our community since 2015" },
  { value: 41, suffix: "", label: "Inclusive homes planned at Adam's Way" },
  { value: 5, prefix: "$", suffix: "M", label: "Community campaign to close the gap" },
];

const Counter = ({ value, prefix = "", suffix = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, value, {
      duration: 2,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setDisplay(v),
    });
    return () => controls.stop();
  }, [inView, value]);

  const formatted =
    value >= 1000
      ? Math.round(display).toLocaleString()
      : display.toFixed(0);

  return (
    <span ref={ref} className="tabular-nums">
      {prefix}
      {formatted}
      {suffix}
    </span>
  );
};

const Impact = () => (
  <section id="impact" data-testid="impact-section" className="relative bg-ink text-bone py-28 lg:py-40 overflow-hidden">
    <div
      className="pointer-events-none absolute -bottom-40 -right-40 w-[38rem] h-[38rem] rounded-full bg-primary/10 blur-3xl"
      aria-hidden="true"
    />
    <div className="max-w-[90rem] mx-auto px-6 lg:px-12 relative">
      <div className="grid grid-cols-12 gap-8 items-end mb-20 lg:mb-28">
        <div className="col-span-12 lg:col-span-7">
          <Reveal>
            <p className="overline-label mb-6 text-primary" data-testid="impact-overline">
              Impact, Measured in Neighbors
            </p>
            <h2
              className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight"
              data-testid="impact-heading"
            >
              Ten years of opening doors,{" "}
              <em className="text-primary font-normal">and we're just getting started.</em>
            </h2>
          </Reveal>
        </div>
        <div className="col-span-12 lg:col-span-4 lg:col-start-9">
          <Reveal delay={0.15}>
            <p className="text-base leading-relaxed text-bone/60">
              TNS Housing Corp grows out of The Next Step Programs' decade of
              service in Bucks County. Every number below is a person, a family,
              a future made possible.
            </p>
          </Reveal>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-14">
        {STATS.map((s, i) => (
          <Reveal key={i} delay={i * 0.1}>
            <div
              className="group border-t border-bone/15 pt-8 transition-colors duration-500 hover:border-primary/70"
              data-testid={`impact-stat-${i}`}
            >
              <p className="font-serif font-light text-5xl lg:text-7xl leading-none text-bone transition-colors duration-500 group-hover:text-primary">
                <Counter value={s.value} prefix={s.prefix} suffix={s.suffix} />
              </p>
              <p className="mt-4 text-sm leading-relaxed text-bone/55 max-w-[16rem]">
                {s.label}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default Impact;
